import React, { useState, useRef, useCallback } from "react";
import ModalWrapper, { ModalContent } from "./ModalWrapper";

const BottomSheet = React.forwardRef(
  (
    {
      fullScreen,
      noFill,
      elevation,
      animate,
      className,
      children,
      slideable = false,
      snapPoints = [50, 100],
      onClose,
      ...rest
    },
    forwardedRef,
  ) => {
    const sheetRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const [currentSnapIndex, setCurrentSnapIndex] = useState(0);
    const [dragOffset, setDragOffset] = useState(0);

    const dragStart = useRef({ y: 0, time: 0 });

    const setRefs = useCallback(
      (node) => {
        sheetRef.current = node;
        if (typeof forwardedRef === "function") {
          forwardedRef(node);
        } else if (forwardedRef) {
          forwardedRef.current = node;
        }
      },
      [forwardedRef],
    );

    const handlePointerDown = (e) => {
      if (!slideable) return;
      setIsDragging(true);
      dragStart.current = { y: e.clientY, time: Date.now() };
      e.target.setPointerCapture(e.pointerId);
    };

    const handlePointerMove = (e) => {
      if (!isDragging) return;
      const deltaY = e.clientY - dragStart.current.y;
      setDragOffset(deltaY);
    };

    const handlePointerUp = (e) => {
      if (!isDragging) return;
      setIsDragging(false);
      e.target.releasePointerCapture(e.pointerId);

      const deltaY = e.clientY - dragStart.current.y;
      const deltaTime = Date.now() - dragStart.current.time;
      const velocity = deltaY / deltaTime;

      if (!sheetRef.current) {
        setDragOffset(0);
        return;
      }

      const windowHeight = window.innerHeight;
      const startVisibleHeight =
        (snapPoints[currentSnapIndex] / 100) * windowHeight;
      const endVisibleHeight = startVisibleHeight - deltaY;

      // Close if swiped down fast or dragged down significantly past the lowest snap point
      if (
        velocity > 0.5 ||
        endVisibleHeight < (snapPoints[0] / 100) * windowHeight * 0.5
      ) {
        setDragOffset(0);
        if (onClose) onClose();
      } else {
        // Find nearest snap point
        let nearestIndex = 0;
        let minDiff = Infinity;
        snapPoints.forEach((pt, idx) => {
          const ptHeight = (pt / 100) * windowHeight;
          const diff = Math.abs(ptHeight - endVisibleHeight);
          if (diff < minDiff) {
            minDiff = diff;
            nearestIndex = idx;
          }
        });
        setCurrentSnapIndex(nearestIndex);
        setDragOffset(0);
      }
    };

    const classes = [
      "ta-bottomsheet",
      !fullScreen &&
        !noFill &&
        elevation &&
        "ta-elevation--shadow ta-elevation_" + elevation,
      className,
    ]
      .filter(Boolean)
      .join(" ");

    let inlineStyle = {};
    if (slideable) {
      const windowHeight =
        typeof window !== "undefined" ? window.innerHeight : 800;
      const targetVh = snapPoints[currentSnapIndex];
      const maxVh = Math.max(...snapPoints);

      inlineStyle.height = `${maxVh}vh`;

      const hideVh = maxVh - targetVh;
      const hidePx = (hideVh / 100) * windowHeight;

      if (animate) {
        const totalTranslate = hidePx + dragOffset;
        const finalTranslate = Math.max(0, totalTranslate);
        inlineStyle.transform = `translate3d(0, ${finalTranslate}px, 0)`;
      }
    }

    return (
      <ModalWrapper animate={animate} {...rest}>
        <div
          ref={setRefs}
          className={classes}
          style={slideable ? inlineStyle : undefined}
          data-fullscreen={fullScreen ? "true" : undefined}
          data-animate={typeof animate === "string" ? animate : undefined}
          data-dragging={isDragging ? "true" : undefined}
        >
          {slideable && (
            <div
              className="ta-bottomsheet_drag-handle"
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerCancel={handlePointerUp}
            />
          )}
          <ModalContent>{children}</ModalContent>
        </div>
      </ModalWrapper>
    );
  },
);

export default BottomSheet;
